import { useSignupActions } from "../useSignupActions";
import { AuthNextStep } from "@sellar-io/common";
import { Button, H2, H5, InputEmail } from "@sellar-io/components";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router";
import { useLocalStorage } from "usehooks-ts";
import { Icon } from "../../../../components";
import { Alert } from "~/components/alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { useEvents, useSellarUser } from "~/hooks";
import { useFirebaseUser } from "../../../../hooks/useFirebaseUser.shared";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../../hooks/useSignupContext.shared";
import sellarLogoNew from "../../../../images/sellar-logo-new.png";
import { getJwtUrl } from "../signupUrl";
import {
  isSignInConnectionError,
  signInConnectionMessage,
} from "../../../../hooks/signInConnection";
import { GoogleEmployeeSignInButton } from "../../signIn/GoogleEmployeeSignInButton";

const getDefaultEmail = (
  queryParamEmail: string | null,
  userEmail?: string | null,
) => {
  if (userEmail) return userEmail;

  return queryParamEmail ?? "";
};
export const UserSignupEmailStep = ({
  supplier,
  setEmail,
  previousEmail,
}: {
  setEmail: (email: string) => void;
  previousEmail?: string;
  supplier?: {
    id: number;
    Business: {
      name: string;
      logoUrl: string | null;
    };
  };
}) => {
  const { track } = useEvents();
  const [, setMagicLinkEmail] = useLocalStorage("magicLinkEmail", "");
  const [searchParams] = useSearchParams();

  const { setStep, showLoginTitle, signupDialogActive, basketTotal } =
    useSignupContext();
  const [, setSignupSupplierId] = useLocalStorage("signupSupplierId", "");
  const [, setOnboardingContinueUrl] = useLocalStorage(
    "onboardingContinueUrl",
    "",
  );

  const showSellarCreditOnboardingParam = searchParams.get(
    "showSellarCreditOnboarding",
  );

  const { signOut } = useSellarUser();
  const { user } = useFirebaseUser();

  const joinBusinessEmail = getDefaultEmail(
    searchParams.get("email"),
    user?.firebaseUser.email,
  );
  const formHook = useForm<{ email: string }>({
    shouldUseNativeValidation: true,
    defaultValues: {
      email: joinBusinessEmail,
    },
  });
  const continueUrl = searchParams.get("continue");
  const businessUserInviteJwt = getJwtUrl(continueUrl) ?? null;
  const {
    loading,
    error,
    setError,
    runSubmission,
    getNextStep,
    continueSignIn,
    createCode,
    trackSignup,
    isInvite,
  } = useSignupActions({ setEmail, previousEmail, businessUserInviteJwt });
  const isContinueUrlToPaymentLink = !!continueUrl?.includes("payment-link");
  const storefrontLink = searchParams.get("storefrontLink");
  useEffect(() => {
    track("Enter Email Page Viewed");
  }, [track]);

  const onSubmit = (newEmail: string) =>
    runSubmission(async (isCurrent) => {
      const saveContinuation = () => {
        if (continueUrl && !continueUrl.includes("jwt")) {
          const storefrontLinkParam =
            searchParams.get("storefrontLink") ??
            searchParams.get("priceTierLink");
          const storefrontLinkQueryString = storefrontLinkParam
            ? `${searchParams.get("queryParameters") ? "&" : "?"}storefrontLink=${storefrontLinkParam}`
            : "";
          setOnboardingContinueUrl(
            `${searchParams.get("continue")}${
              searchParams.get("queryParameters")
                ? `${searchParams.get("queryParameters")}`
                : ""
            }${storefrontLinkQueryString}`,
          );
        }
      };

      if (user && user.firebaseUser.email !== newEmail) {
        try {
          await signOut();
          if (!isCurrent()) return;
        } catch (error) {
          if (!isCurrent()) return;
          setError({
            description: isSignInConnectionError(error)
              ? signInConnectionMessage
              : "We couldn’t switch accounts. Please try again.",
          });
          return;
        }
        setEmail("");
        setError({
          title:
            "The email you've entered is different to your currently authenticated account",
          description:
            "We've logged you out of your other account, please re-enter your sign-in details for the account you'd like to proceed with.",
        });
        return setStep(SignupFlowStep.EmailStep);
      }

      const { nextStep } = await getNextStep({
        email: newEmail,
      });
      if (!isCurrent()) return;
      saveContinuation();
      if (nextStep === AuthNextStep.SignIn) return continueSignIn(newEmail);

      // Match the invite classification used by code creation. Ordinary signup
      // waits for the business/consumer choice before creating an identity/code.
      if (isInvite) {
        await createCode(newEmail);
        if (!isCurrent()) return;
        setEmail(newEmail);
      }
      if (supplier) setSignupSupplierId(supplier.id.toString());
      setMagicLinkEmail(newEmail);
      // Keep the previous code recipient until the next code is created,
      // so editing an unfinished signup can still clean up its old identity.
      setStep(
        isInvite ? SignupFlowStep.MagicLinkSendStep : SignupFlowStep.AboutStep,
      );
      trackSignup(newEmail);
    });

  const getScreenTitle = () => {
    if (supplier) {
      if (basketTotal && signupDialogActive) return "Let's place your order";
      if (storefrontLink)
        return `${showLoginTitle ? "Log in" : "Create an account"} to order from ${supplier.Business.name}'s trade store`;
      return `${showLoginTitle ? "Log in" : "Create an account"} to access ${supplier.Business.name}'s trade pricing`;
    }

    if (showSellarCreditOnboardingParam) {
      return "Log in or create a Sellar account to start using Sellar Credit";
    }

    return "Sellar. Log in or sign up";
  };

  return (
    <div className={"flex h-full w-full flex-col justify-center gap-4"}>
      <div className={"flex flex-col gap-2"}>
        {(!signupDialogActive || supplier) && (
          <div className="mb-4 flex justify-center">
            <img
              alt={"Sellar Logo"}
              src={supplier?.Business.logoUrl ?? sellarLogoNew}
              className={"w-[50px] items-center"}
            />
          </div>
        )}
        <H2 className={"text-center"}>{getScreenTitle()}</H2>
        <p className={"text-center"}>
          Get started by entering your work email address
        </p>
      </div>
      {error && (
        <Alert
          type={"error"}
          title={error.title}
          message={error.description}
          className={"w-full"}
        />
      )}
      <form
        className={"flex flex-col gap-2"}
        onSubmit={(event) => {
          void formHook.handleSubmit((data) => onSubmit(data.email.trim()))(
            event,
          );
        }}
      >
        <InputLabel>
          <H5>Email address</H5>
        </InputLabel>
        <InputContainer>
          <InputEmail
            disabled={loading}
            autoComplete={"username"}
            {...formHook.register("email", { required: true })}
          />
        </InputContainer>
        <Button
          type={"submit"}
          className={"mt-4 w-full"}
          loading={loading}
          disabled={loading}
        >
          <span>Continue</span>
        </Button>
      </form>
      {!supplier && <GoogleEmployeeSignInButton />}
      {isContinueUrlToPaymentLink && (
        <>
          <div className="inline-flex gap-1 text-zinc-700">
            <Icon size={1} icon={"INFORMATION_OUTLINE"} />
            <p>
              Login to your business account with access to Sellar Credit to pay
              for this order
            </p>
          </div>
        </>
      )}
    </div>
  );
};
